import React, { useState } from 'react'
import { ChatInterface } from '../components/chat/ChatInterface'
import { X, Bot } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

export const MobilePreviewPage: React.FC = () => {
  const navigate = useNavigate()
  const [platform, setPlatform] = useState<'ios' | 'android'>('ios')

  const handleStartProcess = () => {
    // Account creation happens through chat
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted via-background to-muted flex flex-col items-center p-4 md:p-8">
      <div className="w-full max-w-6xl">
        {/* Header */}
        <div className="mb-6 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
            Chat-First Mobile Experience
          </h1>
          <p className="text-muted-foreground mb-6">
            Experience our AI-powered 1031 exchange assistant, designed chat-first for iOS and Android
          </p>
        </div>

        {/* Platform Tabs */}
        <div className="mb-8 flex items-center justify-center">
          <div className="inline-flex bg-card rounded-2xl p-1.5 shadow-lg border border-border">
            <button
              onClick={() => setPlatform('ios')}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                platform === 'ios'
                  ? 'bg-foreground text-background shadow-md'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
              style={platform === 'ios' ? { fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif' } : {}}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              <span>iOS</span>
            </button>
            <button
              onClick={() => setPlatform('android')}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                platform === 'android'
                  ? 'bg-[#34A853] text-white shadow-md'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
              style={platform === 'android' ? { fontFamily: 'Roboto, sans-serif' } : {}}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M3.609 1.814L13.792 12 3.609 22.186a1.979 1.979 0 01-.289-1.046V2.86c0-.384.11-.762.289-1.046z"/>
                <path fill="currentColor" d="M13.792 12l3.332-3.332 3.758 2.133a1.978 1.978 0 010 3.398l-3.758 2.133z"/>
                <path fill="currentColor" d="M3.32.768l10.472 5.9L17.124 10l-3.332 3.332L3.32 23.232A1.998 1.998 0 012 21.14V2.86a1.998 1.998 0 011.32-1.892z"/>
                <path fill="currentColor" d="M13.792 12L3.32 1.768a2.015 2.015 0 012.289 0l14.273 8.033z"/>
              </svg>
              <span>Android</span>
            </button>
          </div>
        </div>

        {/* Phone Frame */}
        <div className="flex items-center justify-center mb-8">
          <div className={`relative ${platform === 'ios' ? 'w-[375px]' : 'w-[360px]'} ${platform === 'ios' ? 'h-[812px]' : 'h-[800px]'} mx-auto`}>
            {/* Phone Frame */}
            <div className={`absolute inset-0 rounded-[3rem] ${platform === 'ios' ? 'bg-black' : 'bg-slate-800'} shadow-2xl p-2 ${platform === 'ios' ? 'p-3' : 'p-2.5'}`}>
              {/* Screen Bezel */}
              <div className={`w-full h-full rounded-[2.5rem] ${platform === 'ios' ? 'bg-white' : 'bg-slate-900'} overflow-hidden relative`}>
                {/* Status Bar - iOS Style */}
                {platform === 'ios' && (
                  <div className="h-11 bg-gradient-to-b from-white to-white/95 backdrop-blur-xl flex items-center justify-between px-6 pt-1 pb-0.5 relative z-20" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 4px)' }}>
                    <div className="flex items-center gap-1 text-xs font-semibold text-black" style={{ fontSize: '15px', fontWeight: 600, letterSpacing: '-0.5px' }}>
                      <span>9:41</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4 text-black" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M2 22h20V2z"/>
                      </svg>
                      <svg className="w-5 h-3 text-black" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M1 9l2-2v18l-2-2V9zm4 0l2-2v18l-2-2V9zm4 0l2-2v18l-2-2V9zm4 0l2-2v18l-2-2V9zm4 0l2-2v18l-2-2V9zm4 0l2-2v18l-2-2V9z"/>
                      </svg>
                      <svg className="w-6 h-4 text-black" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M23 7l-7 5 7 5V7zM1 5v14l7-5-7-5z"/>
                      </svg>
                    </div>
                  </div>
                )}

                {/* Status Bar - Android Style */}
                {platform === 'android' && (
                  <div className="h-12 bg-slate-900 flex items-center justify-between px-4 pt-2 pb-1 relative z-20">
                    <div className="flex items-center gap-1 text-xs font-medium text-white">
                      <span style={{ fontSize: '14px' }}>9:41</span>
                      <div className="flex items-center gap-0.5 ml-2">
                        <div className="w-1 h-1.5 bg-green-500 rounded-full"></div>
                        <div className="w-1 h-1.5 bg-green-500 rounded-full"></div>
                        <div className="w-1 h-1.5 bg-green-500 rounded-full"></div>
                        <div className="w-1 h-1.5 bg-slate-400 rounded-full"></div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M1 9l2-2v18l-2-2V9zm4 0l2-2v18l-2-2V9zm4 0l2-2v18l-2-2V9zm4 0l2-2v18l-2-2V9zm4 0l2-2v18l-2-2V9zm4 0l2-2v18l-2-2V9z"/>
                      </svg>
                      <svg className="w-5 h-3 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M23 7l-7 5 7 5V7zM1 5v14l7-5-7-5z"/>
                      </svg>
                    </div>
                  </div>
                )}


                {/* App Content - Chat-First Design */}
                <div className="h-full flex flex-col overflow-hidden" style={{ 
                  fontFamily: platform === 'ios' ? '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", sans-serif' : 'Roboto, sans-serif',
                  background: platform === 'ios' ? 'linear-gradient(to bottom, #f8f9fa, #ffffff)' : '#0f172a'
                }}>
                  {/* iOS Chat Header - Minimal */}
                  {platform === 'ios' && (
                    <div className="bg-white/80 backdrop-blur-xl px-4 pt-3 pb-3 border-b border-slate-200/30" style={{ 
                      paddingTop: '12px',
                      background: 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)'
                    }}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md" style={{
                            borderRadius: '20px',
                            boxShadow: '0 2px 8px rgba(105, 173, 186, 0.3)'
                          }}>
                            <Bot className="h-5 w-5 text-primary-foreground" />
                          </div>
                          <div>
                            <h2 className="text-lg font-semibold text-foreground" style={{ fontWeight: 600, fontSize: '17px', letterSpacing: '-0.3px' }}>Neela</h2>
                            <p className="text-xs text-muted-foreground" style={{ fontSize: '13px', lineHeight: '16px' }}>AI Assistant</p>
                          </div>
                        </div>
                        <button
                          onClick={() => navigate('/')}
                          className="p-2 -mr-2 active:opacity-50 transition-opacity rounded-full hover:bg-muted"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <X className="h-5 w-5 text-muted-foreground" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Android Chat Header - Material Design */}
                  {platform === 'android' && (
                    <div className="bg-slate-900 px-4 py-3 border-b border-slate-800/50 shadow-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg">
                            <Bot className="h-5 w-5 text-primary-foreground" />
                          </div>
                          <div>
                            <h2 className="text-base font-semibold text-white" style={{ fontSize: '16px', fontWeight: 500 }}>Neela</h2>
                            <p className="text-xs text-slate-400" style={{ fontSize: '12px' }}>AI Assistant</p>
                          </div>
                        </div>
                        <button
                          onClick={() => navigate('/')}
                          className="p-2 hover:bg-slate-800 rounded-full transition-colors"
                        >
                          <X className="h-5 w-5 text-slate-300" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Chat Interface - Full Screen Chat-First */}
                  <div 
                    className="flex-1 overflow-hidden" 
                    style={{ 
                      WebkitOverflowScrolling: 'touch',
                      background: platform === 'ios' ? '#ffffff' : '#0f172a'
                    }}
                  >
                    <ChatInterface 
                      onStartProcess={handleStartProcess}
                      className="h-full"
                    />
                  </div>

                  {/* iOS Home Indicator */}
                  {platform === 'ios' && (
                    <div className="h-8 bg-white flex items-center justify-center pb-2" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)' }}>
                      <div className="w-32 h-1 bg-slate-300 rounded-full" style={{ backgroundColor: '#cbd5e1' }}></div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* App Store Badges */}
            <div className="absolute -bottom-20 left-1/2 -translate-x-1/2 flex items-center gap-4 z-10">
              {platform === 'ios' ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="bg-foreground rounded-xl px-6 py-3 flex items-center gap-3 shadow-lg">
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="white">
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                    </svg>
                    <div className="text-background">
                      <div className="text-xs">Download on the</div>
                      <div className="text-lg font-semibold">App Store</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <div className="bg-foreground rounded-xl px-6 py-3 flex items-center gap-3 shadow-lg">
                    <svg className="w-10 h-10" viewBox="0 0 24 24">
                      <path fill="#34A853" d="M3.609 1.814L13.792 12 3.609 22.186a1.979 1.979 0 01-.289-1.046V2.86c0-.384.11-.762.289-1.046z"/>
                      <path fill="#FBBC04" d="M13.792 12l3.332-3.332 3.758 2.133a1.978 1.978 0 010 3.398l-3.758 2.133z"/>
                      <path fill="#4285F4" d="M3.32.768l10.472 5.9L17.124 10l-3.332 3.332L3.32 23.232A1.998 1.998 0 012 21.14V2.86a1.998 1.998 0 011.32-1.892z"/>
                      <path fill="#34A853" d="M13.792 12L3.32 1.768a2.015 2.015 0 012.289 0l14.273 8.033z"/>
                    </svg>
                    <div className="text-background">
                      <div className="text-xs">GET IT ON</div>
                      <div className="text-lg font-semibold">Google Play</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Features List - Chat-First Focus */}
        <div className="mt-40 max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
              Designed Chat-First
            </h2>
            <p className="text-muted-foreground">
              Every interaction starts with a conversation. Neela guides you through your entire 1031 exchange journey.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 bg-card rounded-2xl shadow-lg border border-border">
              <div className="w-14 h-14 bg-gradient-to-br from-primary to-primary/80 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Bot className="h-7 w-7 text-primary-foreground" />
              </div>
              <h3 className="font-bold text-lg text-foreground mb-2 text-center">AI-Powered Chat</h3>
              <p className="text-sm text-muted-foreground text-center leading-relaxed">Start your exchange with a simple conversation. Neela understands context and guides you step-by-step.</p>
            </div>
            <div className="p-6 bg-card rounded-2xl shadow-lg border border-border">
              <div className="w-14 h-14 bg-gradient-to-br from-primary to-primary/80 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <svg className="h-7 w-7 text-primary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="font-bold text-lg text-foreground mb-2 text-center">Voice & Text</h3>
              <p className="text-sm text-muted-foreground text-center leading-relaxed">Choose your preferred way to interact. Speak naturally or type your questions—Neela adapts to you.</p>
            </div>
            <div className="p-6 bg-card rounded-2xl shadow-lg border border-border">
              <div className="w-14 h-14 bg-gradient-to-br from-primary to-primary/80 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <svg className="h-7 w-7 text-primary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="font-bold text-lg text-foreground mb-2 text-center">Secure & Private</h3>
              <p className="text-sm text-muted-foreground text-center leading-relaxed">Bank-level encryption keeps your conversations and data secure. Your privacy is our priority.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

